import {
  AddWorkflowNodeRequest,
  UpdateWorkflowNodeRequest,
  UpdateWorkflowRequest,
  WorkflowNodeType,
  WorkflowsService,
  WorkspaceResult,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NodeToolbar, useNodeId, useNodes } from '@xyflow/react';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import WorkspaceSelector from './WorkspaceSelector';

export type ModalHandlers = {
  handleSave: (selectedWorkspace: WorkspaceResult | null) => void;
  handleCancel: () => void;
};

export default function NodeWithToolbar({
  setNodes,
  allowDelete = true,
  workflowId,
  allowAdd = true,
}: {
  setNodes: any;
  allowDelete?: boolean;
  workflowId: string;
  allowAdd?: boolean;
}) {
  const nodeId = useNodeId();
  const nodes = useNodes();
  const queryClient = useQueryClient();
  const workflow = useStore($workflow);
  const [openWorkspaceSelectorModal, setOpenWorkspaceSelectorModal] =
    useState(false);

  // Define the type for modal handlers
  const [modalHandlers, setModalHandlers] = useState<ModalHandlers | null>(
    null,
  );
  const isWorkflowPublished = !!workflow?.isActive;
  // mutation to create a new workspace
  const addNode = useMutation({
    mutationFn: async (payload: AddWorkflowNodeRequest) => {
      // call the api to create a new workspace
      $addingNode.set(true);
      const res = await WorkflowsService.addWorkflowNode(workflowId, payload);
      return res;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
      $addingNode.set(false);
    },
    onError: error => {
      console.error(error);
      $addingNode.set(false);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      const res = await WorkflowsService.updateWorkflowNode(
        workflowId,
        payload.nodeId,
        payload.req,
      );
      return res;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
      // Added: force refetch of workflow and update nodes
      const updatedWorkflow = await WorkflowsService.getWorkflow(workflowId);
      if (updatedWorkflow?.nodes) {
        const formattedNodes = updatedWorkflow.nodes.map((node: any) => ({
          id: node.id,
          type: node?.type,
          position: { x: node.position.x, y: node.position.y },
          // FIX: include the node name from the workflow data so it persists
          data: { ...node?.data, name: node?.name, order: node?.order || null },
        }));
        setNodes(formattedNodes);
      }
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // mutation to update a node
  const deleteNode = useMutation({
    mutationFn: async (nodeId: string) => {
      // call the api to create a new workspace
      $deletingNode.set(true);
      const res = await WorkflowsService.removeWorkflowNode(workflowId, nodeId);
      return res;
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflowId],
      });
      $deletingNode.set(false);
      closeNodeConfigurationDrawer();
    },
    onError: error => {
      console.error(error);
      $deletingNode.set(false);
      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  async function addNewNode(type: WorkflowNodeType) {
    // Find the current node
    const currentNodeIndex = JSON.parse(JSON.stringify(nodes)).findIndex(
      (node: any) => node.id === nodeId,
    );

    if (currentNodeIndex === -1) return;

    // Get the current node's details
    const currentNode = nodes[currentNodeIndex];

    // Calculate new y position for the new node (below the current node by 100 units)
    const newYPosition = currentNode?.position?.y + 160;

    const newNode = await addNode.mutateAsync({
      type,
      position: { x: currentNode.position.x, y: newYPosition },
      data: null,
      order: currentNodeIndex + 1,
      previousNodeId: currentNode.id,
    });
    openNodeConfigurationDrawer(newNode.workflowNode);

    if (newNode?.workflowNode) {
      openNodeConfigurationDrawer({
        ...newNode.workflowNode,
        data: newNode.workflowNode.data || {}, // Ensure data is not null
      });
    }
  }

  // mutation to update a workflow
  const updateWorkflow = useMutation({
    mutationFn: async (payload: UpdateWorkflowRequest) => {
      // call the api to create a new workspace
      if (workflow) {
        const res = await WorkflowsService.updateWorkflow(workflow.id, payload);
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', workflow?.id],
      });
      toast('Configured workspace successfully.', {
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Function to handle showing the modal
  const showWorkspaceSelectionModal = () => {
    return new Promise(resolve => {
      setOpenWorkspaceSelectorModal(true);

      const handleSave = async (workspace: WorkspaceResult | null) => {
        // Save the selected workspace
        if (workspace) {
          await updateWorkflow.mutateAsync({
            workspaceId: workspace?.workspace.id,
          });
        }

        setOpenWorkspaceSelectorModal(false);
        resolve(workspace);
      };

      const handleCancel = () => {
        setOpenWorkspaceSelectorModal(false);
        resolve(null); // Resolve with null if canceled
      };

      // Attach event listeners to buttons inside the modal
      // Pass callbacks as props to the modal
      setModalHandlers({ handleSave, handleCancel });
    });
  };

  const handleDropdownClick = async (nodeType: WorkflowNodeType) => {
    if (
      !workflow?.workspaceId &&
      (nodeType === 'ANALYZER' ||
        nodeType === 'DATA_FLAGGER' ||
        nodeType === 'DATA_INGESTION')
    ) {
      const selectedWorkspace = await showWorkspaceSelectionModal();
      if (selectedWorkspace) {
        // Proceed with adding the node after workspace selection
        await addNewNode(nodeType);
      }
    } else {
      // Directly add the node if workspaceId exists
      await addNewNode(nodeType);
    }
  };

  // console.log('nodeid', nodeId, nodes);
  return (
    <>
      <TooltipProvider delayDuration={50}>
        <NodeToolbar isVisible={true}>
          <DropdownMenu>
            <DropdownMenuTrigger>
              {allowAdd && !isWorkflowPublished && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-black dark:bg-white">
                    <p>Add new node</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Select Node</DropdownMenuLabel>
              <DropdownMenuSeparator />

              {/* <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  await addNewNode('SFTP_UPLOAD');
                }}
                disabled={isWorkflowPublished || addNode.isPending}              >
                SFTP Upload
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  await addNewNode('SFTP_DOWNLOAD');
                }}
                disabled={isWorkflowPublished || addNode.isPending}              >
                SFTP Download
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  //await addNewNode('DATA_INGESTION');
                  await handleDropdownClick('DATA_INGESTION');
                }}
                disabled={isWorkflowPublished || addNode.isPending}              >
                Data Ingestion
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  //await addNewNode('ANALYZER');
                  await handleDropdownClick('ANALYZER');
                }}
                disabled={isWorkflowPublished || addNode.isPending}              >
                Analyzer
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  // await addNewNode('DATA_FLAGGER');
                  await handleDropdownClick('DATA_FLAGGER');
                }}
                // disabled={true}
                disabled={isWorkflowPublished}
              >
                Data Flagger
              </DropdownMenuItem> */}

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  await handleDropdownClick('TIMER');
                }}
                disabled={isWorkflowPublished || addNode.isPending}
              >
                Timer
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  await handleDropdownClick('SEND_SMS');
                }}
                disabled={addNode.isPending}
              >
                Send SMS
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  if (isWorkflowPublished) return;
                  await addNewNode('EMAIL_NOTIFICATION');
                }}
                disabled={isWorkflowPublished || addNode.isPending}
              >
                Send Email
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={async () => {
                  await addNewNode('AI_OUTBOUND_PHONE_CALL');
                }}
              >
                Automated Phone Call
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {allowDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="ml-2"
                  disabled={isWorkflowPublished || deleteNode.isPending}
                  onClick={async () => {
                    if (isWorkflowPublished) return;
                    if (nodeId) {
                      await deleteNode.mutateAsync(nodeId);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-black dark:bg-white">
                <p>Delete this node</p>
              </TooltipContent>
            </Tooltip>
          )}
        </NodeToolbar>
      </TooltipProvider>

      <WorkspaceSelector
        open={openWorkspaceSelectorModal}
        setOpen={setOpenWorkspaceSelectorModal}
        modalHandlers={modalHandlers}
        isSaving={updateWorkflow.isPending}
      />
    </>
  );
}
